import React, { useState } from 'react'
import { Box, Button, CircularProgress, Typography } from '@material-ui/core'
import {
    CheckCircleOutline as SuccessIcon,
    ErrorOutline as ErrorIcon,
} from '@material-ui/icons'

import { RentRollSummaryResponse } from '../../types'
import { useRentRollRootStyles } from './styles'

interface RentRollReviewProps {
    summary: RentRollSummaryResponse
    isConfirming?: boolean
    isReportingIssue?: boolean
    isDeleting?: boolean
    onConfirm: () => Promise<void>
    onReportIssue: () => Promise<{ message: string }>
    onUploadDifferentFile: () => void
    isFailure?: boolean
    failureMessage?: string | null
}

export const RentRollReview: React.FC<RentRollReviewProps> = ({
    summary,
    isConfirming = false,
    isReportingIssue = false,
    isDeleting = false,
    onConfirm,
    onReportIssue,
    onUploadDifferentFile,
    isFailure = false,
    failureMessage = null,
}) => {
    const classes = useRentRollRootStyles({
        headerHeight: 0,
        collapsableSelectorHeight: 0,
    })
    const [ticketSubmitted, setTicketSubmitted] = useState(false)
    const [ticketMessage, setTicketMessage] = useState<string | null>(null)

    const totalResidents =
        summary.current_resident_count + summary.future_resident_count
    const looksSuspicious = !isFailure && totalResidents === 0
    const actionsDisabled = isConfirming || isReportingIssue || isDeleting

    const handleReportIssue = async () => {
        const response = await onReportIssue()
        setTicketSubmitted(true)
        setTicketMessage(response.message)
    }

    if (ticketSubmitted) {
        return (
            <Box className={classes.reviewPanel}>
                <Box className={classes.reviewCard}>
                    <SuccessIcon className={classes.reviewIconSuccess} />
                    <Typography variant="h6" gutterBottom>
                        Support ticket submitted
                    </Typography>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        align="center"
                    >
                        {ticketMessage ??
                            'Our team will review this rent roll and follow up with you.'}
                    </Typography>
                </Box>
            </Box>
        )
    }

    return (
        <Box className={classes.reviewPanel}>
            <Box className={classes.reviewCard}>
                {isFailure ? (
                    <ErrorIcon className={classes.reviewIconError} />
                ) : (
                    <SuccessIcon className={classes.reviewIconSuccess} />
                )}

                <Typography variant="h6">
                    {isFailure
                        ? 'We had trouble reading your rent roll'
                        : 'Rent roll analyzed'}
                </Typography>

                {summary.filename && (
                    <Typography variant="body2" color="textSecondary">
                        {summary.filename}
                    </Typography>
                )}

                {isFailure ? (
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        align="center"
                    >
                        {failureMessage ??
                            'We could not analyze this file automatically. Send a support ticket and we will take a look.'}
                    </Typography>
                ) : (
                    <>
                        <Box className={classes.reviewStats}>
                            <Box className={classes.reviewStatItem}>
                                <Typography className={classes.reviewStatValue}>
                                    {summary.current_resident_count}
                                </Typography>
                                <Typography className={classes.reviewStatLabel}>
                                    Current residents
                                </Typography>
                            </Box>
                            <Box className={classes.reviewStatItem}>
                                <Typography className={classes.reviewStatValue}>
                                    {summary.future_resident_count}
                                </Typography>
                                <Typography className={classes.reviewStatLabel}>
                                    Future residents
                                </Typography>
                            </Box>
                        </Box>

                        {looksSuspicious ? (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                align="center"
                            >
                                We did not find any residents in this file. If
                                that does not look right, send a support ticket.
                            </Typography>
                        ) : (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                align="center"
                            >
                                If these numbers look right, we will process
                                your rent roll and load resident data.
                            </Typography>
                        )}
                    </>
                )}

                <Box className={classes.reviewActions}>
                    {!isFailure && (
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.reviewActionButton}
                            disabled={actionsDisabled || looksSuspicious}
                            onClick={() => {
                                void onConfirm()
                            }}
                        >
                            {isConfirming ? (
                                <>
                                    <CircularProgress
                                        size={18}
                                        style={{ marginRight: 8 }}
                                    />
                                    Starting...
                                </>
                            ) : (
                                'Looks good, process'
                            )}
                        </Button>
                    )}

                    <Button
                        variant={
                            isFailure || looksSuspicious
                                ? 'contained'
                                : 'outlined'
                        }
                        color="primary"
                        className={classes.reviewActionButton}
                        disabled={actionsDisabled}
                        onClick={() => {
                            void handleReportIssue()
                        }}
                    >
                        {isReportingIssue ? (
                            <>
                                <CircularProgress
                                    size={18}
                                    style={{ marginRight: 8 }}
                                />
                                Submitting...
                            </>
                        ) : (
                            "Something doesn't look right — Send Support Ticket"
                        )}
                    </Button>
                </Box>

                {!isFailure && (
                    <Box className={classes.reviewSecondaryActions}>
                        <Button
                            size="small"
                            color="primary"
                            disabled={actionsDisabled}
                            onClick={onUploadDifferentFile}
                        >
                            {isDeleting
                                ? 'Removing file...'
                                : 'Upload a different file'}
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    )
}
